/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import { END } from 'redux-saga';
import {
    put, call, fork, take, select, takeLatest,
} from 'redux-saga/effects';

import sdkFeatureFlags from 'feature-flags-sdk';
import configClient from 'mbp-config-server-api';
import mbpLogger from 'mbp-logger';
import mbpUtil from 'mbp-api-util';

import memberDucks from '../../../Member/ducks';

import * as configActions from './Config-Actions';
import * as configHelpers from './Config-Helpers';
import * as configSelector from './Config-Selectors';
import { getSSRConfig } from '../../App-Selectors';
import { logUserLoginInSessionReady } from '../../../Member/ducks/Auth/Auth-Actions';
import memCache from '../../../../../app/helpers/memCache/memCache';
import getCookie from '../../../../../app/helpers/common/storage/cookieStorage';

const {
    common: {
        commonSelectors: {
            getProfileInfo,
            getUserType,
            getUserRole,
        },
    },
    profile: {
        profileActions: {
            setProfileEmailId,
            loadedProfileUserRole,
        },
    },
} = memberDucks;

function* getConfig({ config, domain }) {
    try {
        mbpLogger.logDebug({
            config,
            domain,
            function: 'getConfig',
            appName: process.env.npm_package_name,
            module: 'mbp-pwa-ui',
            message: 'Get Config | START',
        });

        const host = (typeof window !== 'undefined') ? mbpUtil.getEnv('APP_WC_HOST') : mbpUtil.getEnv('APP_CONFIG_HOST_SSR');
        const topic = config.topic;
        const branch = config.branch || 'master';

        const cacheKey = host + topic + branch;

        // lookup
        let configResponse = memCache.get(cacheKey);
        if (!configResponse) {
            configResponse = yield call(
                configClient.getConfig,
                {
                    host,
                },
                topic,
                branch,
            );

            if (configResponse.status === 200) {
                memCache.set(cacheKey, configResponse);
            }
        }

        mbpLogger.logDebug({
            host,
            configResponse,
            function: 'getConfig',
            appName: process.env.npm_package_name,
            module: 'mbp-pwa-ui',
            message: 'Config Service Response',
        });

        if (configResponse && configResponse.data && configResponse.data.propertySources.length) {
            const [configPropertySource] = configResponse.data.propertySources;

            mbpLogger.logDebug({
                configPropertySource,
                function: 'getConfig',
                appName: process.env.npm_package_name,
                module: 'mbp-pwa-ui',
                message: 'Config data found.',
            });

            return configHelpers.parseConfig(configPropertySource.source, domain);
        }
    } catch (ex) {
        mbpLogger.logError({
            config,
            domain,
            function: 'getConfig',
            appName: process.env.npm_package_name,
            module: 'mbp-pwa-ui',
            jsError: ex,
            message: 'Failed to get config data from service.',
        });
    }

    return {};
}

function* onLoadConfig({ topic, branch, brand }) {
    let config = {};

    try {
        mbpLogger.logDebug({
            topic,
            branch,
            brand,
            function: 'onLoadConfig',
            appName: process.env.npm_package_name,
            module: 'mbp-pwa-ui',
            message: 'Load Config | START',
        });

        config = yield call(getConfig, {
            config: {
                topic,
                branch,
            },
            domain: brand.domain,
        });
        yield put(configActions.configLoaded(brand.domain, topic, config));
    } catch (ex) {
        yield put(configActions.configLoaded(brand.domain, topic, config));
        mbpLogger.logError({
            topic,
            branch,
            brand,
            function: 'onLoadConfig',
            appName: process.env.npm_package_name,
            module: 'mbp-pwa-ui',
            jsError: ex,
            message: 'Failed to load config data.',
        });
    }

    return config;
}

function* getFlags(brand) {
    let flags = {};
    const profile = yield select(getProfileInfo);
    const {
        deviceType, userKey, isBot, userAgent,
    } = yield select(getSSRConfig);
    const isSSR = (typeof window === 'undefined');
    let sessionUserKey = userKey;

    if (!isSSR) {
        sessionUserKey = configHelpers.getUserKey(sessionUserKey);
    }

    const userType = yield select(getUserType);
    const userStatus = yield select(getUserRole);
    const email = (profile.email) ? profile.email : '';
    const browser = yield call(configHelpers.getBrowser);
    const operatingSystem = yield call(configHelpers.getOperatingSystem);
    // const version = siteVariant || (yield call(configHelpers.getAppVersion));
    const version = yield call(getCookie, 'route') || 'ui';

    if (sessionUserKey || isSSR) {
        const payload = {
            userKey: sessionUserKey || 'anonymous',
            userType: userType || 'G',
            userStatus,
            email,
            firstName: profile.firstName,
            lastName: profile.lastName,
            browser,
            operatingSystem,
            brand,
            version,
            device: deviceType,
            isBot,
            userAgent,
        };

        // Uat2 have different repo that listen the flags
        const appGraphqlEnv = mbpUtil.getEnv('APP_VERSION_ENV');
        let topicFlag = 'mbp-ui';
        if (appGraphqlEnv === 'uat2') {
            topicFlag = 'mbp-ui-beta';
        }

        const featureFlagsResponse = yield call(sdkFeatureFlags.getFeatureFlags, {}, topicFlag, payload);
        flags = featureFlagsResponse.data;
    }
    return flags;
}

function* onLoadFlags({
    brand,
}) {
    const topic = 'app-feature-flags';
    let flags = {};
    try {
        mbpLogger.logDebug({
            brand,
            function: 'onLoadFlags',
            appName: process.env.npm_package_name,
            module: 'mbp-pwa-ui',
            message: 'Load Flag | START',
        });
        if (brand && brand.domain) {
            const { domain } = brand;
            flags = yield call(getFlags, domain);

            if (flags['is-feature-flags-disabled']) {
                yield put(configActions.configLoaded(domain, topic, {}));
                return {};
            }
            yield put(configActions.configLoaded(domain, topic, flags));
        }
    } catch (ex) {
        yield put(configActions.configLoaded(brand.domain, topic, flags));
        mbpLogger.logError({
            topic,
            brand,
            function: 'onLoadFlags',
            appName: process.env.npm_package_name,
            module: 'mbp-pwa-ui',
            jsError: ex,
            message: 'Failed to load flag data.',
        });
    }
    return flags;
}

function* watchLoadConfigs() {
    let action = yield take(configActions.loadConfig().type);

    while (action !== END) {
        yield fork(onLoadConfig, action.payload);
        action = yield take(configActions.loadConfig().type);
    }
}

function* workerHandleUpdateFeatureFlags() {
    const brand = yield select(configSelector.getBrand);
    yield fork(onLoadFlags, { brand });
}

function* watchActionsToUpdateFeatureFlags() {
    yield takeLatest([
        setProfileEmailId().type,
        loadedProfileUserRole().type,
        logUserLoginInSessionReady().type,
    ], workerHandleUpdateFeatureFlags);
}

const watchers = [
    fork(watchLoadConfigs),
    fork(watchActionsToUpdateFeatureFlags),
];

export {
    getFlags,
    onLoadFlags,
    getConfig,
    onLoadConfig,
    watchers,
};
